// AlphaLens API Client

const API_URL = import.meta.env.VITE_API_URL || ''

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('alphalens_token')
  }

  setToken(token) {
    this.token = token
    if (token) {
      localStorage.setItem('alphalens_token', token)
    } else {
      localStorage.removeItem('alphalens_token')
    }
  }

  getToken() {
    return this.token
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    })

    const data = await response.json().catch(() => null)

    if (!response.ok) {
      const error = new Error(data?.error || 'Request failed')
      error.code = data?.code || 'UNKNOWN_ERROR'
      error.status = response.status
      throw error
    }

    return data
  }

  // Auth endpoints
  async register(email, password) {
    const data = await this.request('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    this.setToken(data.token)
    return data
  }

  async login(email, password) {
    const data = await this.request('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    this.setToken(data.token)
    return data
  }

  async logout() {
    try {
      await this.request('/api/v1/auth/logout', { method: 'POST' })
    } finally {
      this.setToken(null)
    }
  }

  async getMe() {
    return this.request('/api/v1/auth/me')
  }

  // Billing endpoints
  async createCheckout(tier) {
    return this.request('/api/v1/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    })
  }

  async createPortal() {
    return this.request('/api/v1/billing/portal', {
      method: 'POST',
    })
  }

  // Stats (public)
  async getStats() {
    return this.request('/api/v1/stats')
  }

  // Track record (public)
  async getTrackRecord() {
    return this.request('/api/v1/track-record')
  }

  // Live signals (public)
  async getSignals(limit = 20) {
    return this.request(`/api/v1/signals?limit=${limit}`)
  }

  // Alerts (public)
  async getAlerts(limit = 10) {
    return this.request(`/api/v1/alerts?limit=${limit}`)
  }

  // Deliberation endpoints (session-authenticated)
  async deliberate(query, options = {}) {
    const body = { query, ...options }
    return this.request('/api/v1/deliberate', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  // Council mode deliberation (Elite only)
  async deliberateCouncil(query, analysts, judge = 'opus') {
    return this.request('/api/v1/deliberate', {
      method: 'POST',
      body: JSON.stringify({
        query,
        mode: 'council',
        analysts,
        judge,
      }),
    })
  }

  // Titans mode - returns debate_id, rounds come via WebSocket
  async deliberateTitans(query) {
    return this.request('/api/v1/deliberate', {
      method: 'POST',
      body: JSON.stringify({
        query,
        mode: 'titans',
      }),
    })
  }

  // WebSocket for Titans streaming
  connectTitansWs(debateId, onRound, onComplete, onError) {
    const wsUrl = import.meta.env.VITE_WS_URL || API_URL.replace('https', 'wss').replace('http', 'ws')
    const ws = new WebSocket(`${wsUrl}/ws/titans/${debateId}?token=${this.token}`)

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)
      if (data.type === 'round') {
        onRound(data.round)
      } else if (data.type === 'complete') {
        onComplete(data)
        ws.close()
      } else if (data.type === 'error') {
        onError(new Error(data.message))
        ws.close()
      }
    }

    ws.onerror = (err) => {
      onError(err)
    }

    return ws
  }

  // Voting endpoints
  async submitVote(debateId, winner) {
    return this.request('/api/v1/votes', {
      method: 'POST',
      body: JSON.stringify({ debate_id: debateId, winner }),
    })
  }

  async getVotes(debateId) {
    return this.request(`/api/v1/votes/${debateId}`)
  }

  // Model-specific track record (Elite)
  async getModelTrackRecord(model, topic = null) {
    let url = `/api/v1/track-record/${model}`
    if (topic) {
      url += `?topic=${encodeURIComponent(topic)}`
    }
    return this.request(url)
  }

  // Legacy verdict endpoint (API key required - Elite tier only)
  async getVerdict(query) {
    return this.request(`/api/v1/verdict?query=${encodeURIComponent(query)}`)
  }
}

export const api = new ApiClient()
export default api
